import type { MetaLine } from "@dto/records";
import type { RowSelectionState } from "@tanstack/react-table";
import type { CellPosition } from "./GridCell";
import { NEW_ROW_ID } from "./newRowId";

// What the grid state points at, by row index
export interface RowAnchors {
    focusPos: CellPosition[] | null;
    editingPos: CellPosition | null;
    selectionAnchor: CellPosition | null;
    rowSelection: RowSelectionState;
}

// Moves the anchors along with their records when a refresh changes the rows
// (a new record sorted on top shifts all of them): rows are identified by their
// index, so they would otherwise stay on whatever record took this index. An anchor
// on a record no longer displayed is dropped. Whatever did not move keeps its
// reference, so that the DOM focus stays where it is (see syncCellFocus).
export function remapByRecord(
    anchors: RowAnchors,
    previous: Map<string, MetaLine>,
    next: Map<string, MetaLine>,
    rowIdentity: (metaLine: MetaLine) => string,
): RowAnchors {
    const rowIdByIdentity = new Map<string, string>();
    next.forEach((metaLine, rowId) => {
        const identity = rowId === NEW_ROW_ID ? "" : rowIdentity(metaLine);
        if (identity && !rowIdByIdentity.has(identity)) {
            rowIdByIdentity.set(identity, rowId);
        }
    });

    // The row of the same record, undefined if it is gone. A row which cannot be
    // identified stays as it is, and so does the creation row, which never moves.
    const remapRow = (rowId: string): string | undefined => {
        const metaLine = rowId === NEW_ROW_ID ? undefined : previous.get(rowId);
        const identity = metaLine ? rowIdentity(metaLine) : "";
        return identity ? rowIdByIdentity.get(identity) : rowId;
    };

    const remapPosition = (position: CellPosition | null): CellPosition | null => {
        if (!position) {
            return null;
        }
        const rowId = remapRow(position.rowId);
        if (rowId === undefined) {
            return null;
        }
        return rowId === position.rowId ? position : { rowId, column: position.column };
    };

    const remapPositions = (positions: CellPosition[] | null): CellPosition[] | null => {
        if (!positions) {
            return null;
        }
        const remapped = positions.map(remapPosition);
        if (remapped.every((position, index) => position === positions[index])) {
            return positions;
        }
        const kept = remapped.filter((position): position is CellPosition => position !== null);
        return kept.length > 0 ? kept : null;
    };

    const remapSelection = (selection: RowSelectionState): RowSelectionState => {
        let changed = false;
        const remapped: RowSelectionState = {};
        Object.keys(selection).forEach((rowId) => {
            const newRowId = remapRow(rowId);
            changed = changed || newRowId !== rowId;
            if (newRowId !== undefined) {
                remapped[newRowId] = selection[rowId];
            }
        });
        return changed ? remapped : selection;
    };

    return {
        focusPos: remapPositions(anchors.focusPos),
        editingPos: remapPosition(anchors.editingPos),
        selectionAnchor: remapPosition(anchors.selectionAnchor),
        rowSelection: remapSelection(anchors.rowSelection),
    };
}

// metaLines is rebuilt from the lines of the view, which do not hold the creation
// row: it must be carried over, or its cells fall back to empty values.
export function withNewRow(next: Map<string, MetaLine>, previous: Map<string, MetaLine>): Map<string, MetaLine> {
    const newRow = previous.get(NEW_ROW_ID);
    if (newRow) {
        next.set(NEW_ROW_ID, newRow);
    }
    return next;
}

// The positions out of the creation row (the same reference if there is none in it)
export function stripNewRow(positions: CellPosition[] | null): CellPosition[] | null {
    if (!positions || !positions.some(position => position.rowId === NEW_ROW_ID)) {
        return positions;
    }
    const kept = positions.filter(position => position.rowId !== NEW_ROW_ID);
    return kept.length > 0 ? kept : null;
}
